import React, { useState, useEffect } from 'react';
import './SettingsPanel.css';

const DEFAULT_COLORS = {
  userBackground: '#007bff',
  userText: '#ffffff',
  aiBackground: '#ffffff',
  aiText: '#000000',
  iconColor: '#00ccff'
};

// Add default model
const DEFAULT_MODEL = 'anthropic'; // or 'llama' as default

const SettingsPanel = ({ 
  isOpen, 
  onClose, 
  onExpertiseLevelChange, 
  currentExpertiseLevel,
  messages,
  onGenerateImage,
  currentLanguage,
  onLanguageChange,
  translations: t,
  currentModel,            // Add this prop
  onModelChange,           // Add this prop
  selectedVoice,
  setSelectedVoice,
}) => {
  // Add dark mode state
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode === null ? true : savedMode === 'true';  // Default to true if not set
  });

  // Effect for dark mode
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    localStorage.setItem('darkMode', isDarkMode);
  }, [isDarkMode]);

  // Use existing settings modal content but in panel format
  const expertiseLevels = [
    { value: 'novice', label: t('novice') },
    { value: 'student', label: t('student') },
    { value: 'professional', label: t('professional') },
    { value: 'researcher', label: t('researcher') },
  ];

  // Add color state management
  const [colors, setColors] = useState(() => {
    const savedColors = localStorage.getItem('chatColors');
    return savedColors ? JSON.parse(savedColors) : DEFAULT_COLORS;
  });

  // Save colors to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('chatColors', JSON.stringify(colors));
    // Update CSS variables for real-time color changes
    document.documentElement.style.setProperty('--user-msg-bg', colors.userBackground);
    document.documentElement.style.setProperty('--user-msg-text', colors.userText);
    document.documentElement.style.setProperty('--ai-msg-bg', colors.aiBackground);
    document.documentElement.style.setProperty('--ai-msg-text', colors.aiText);
    document.documentElement.style.setProperty('--icon-color', colors.iconColor);
  }, [colors]);

  const handleColorChange = (colorKey, value) => {
    setColors(prev => ({
      ...prev,
      [colorKey]: value
    }));
  };

  const handleResetColors = () => {
    setColors(DEFAULT_COLORS);
  };

  // Add language options
  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'hi', name: 'हिंदी', flag: '🇮🇳' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'pt', name: 'Português', flag: '🇵🇹' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' }
  ];

  // Add state for the selected model
  const [selectedModel, setSelectedModel] = useState(() => {
    const savedModel = localStorage.getItem('selectedModel');
    return savedModel || currentModel || DEFAULT_MODEL;
  });

  // Save selected model to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('selectedModel', selectedModel);
    // Inform parent component about the change
    onModelChange(selectedModel);
  }, [selectedModel]);

  const voiceOptions = [
    { name: 'Ava', value: 'en-US-AvaMultilingualNeural' },
    { name: 'Andrew', value: 'en-US-AndrewMultilingualNeural' },
    { name: 'Brandon', value: 'en-US-BrandonMultilingualNeural' },
    { name: 'Amanda', value: 'en-US-AmandaMultilingualNeural' },
  ];

  return (
    <div className={`settings-panel ${isOpen ? 'open' : ''}`}>
      <div className="settings-panel-header">
        <h2>{t('settings')}</h2>
      </div>
      
      <div className="settings-panel-content">
        <div className="settings-section">
          <div className="dark-mode-toggle">
            <label htmlFor="dark-mode">{t('darkMode')}</label>
            <div className="toggle-switch">
              <input
                type="checkbox"
                id="dark-mode"
                checked={isDarkMode}
                onChange={(e) => setIsDarkMode(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </div>
          </div>
        </div>

        <div className="settings-section">
          <button className="generate-image-button" onClick={onGenerateImage}>
            {t('generateImage')}
          </button>
        </div>

        <div className="settings-section">
          <label htmlFor="expertise-level">{t('expertiseLevel')}</label>
          <select
            id="expertise-level"
            value={currentExpertiseLevel}
            onChange={(e) => onExpertiseLevelChange(e.target.value)}
          >
            {expertiseLevels.map((level) => (
              <option key={level.value} value={level.value}>
                {level.label}
              </option>
            ))}
          </select>
        </div>

        <div className="settings-section">
          <h3>{t('chatColors')}</h3>
          <div className="color-picker-container">
            <div className="color-picker-group">
              <label htmlFor="userBackground">{t('userMsgBg')}</label>
              <input
                type="color"
                id="userBackground"
                value={colors.userBackground}
                onChange={(e) => handleColorChange('userBackground', e.target.value)}
              />
            </div>

            <div className="color-picker-group">
              <label htmlFor="userText">{t('userMsgText')}</label>
              <input
                type="color"
                id="userText"
                value={colors.userText}
                onChange={(e) => handleColorChange('userText', e.target.value)}
              />
            </div>

            <div className="color-picker-group">
              <label htmlFor="aiBackground">{t('aiMsgBg')}</label>
              <input
                type="color"
                id="aiBackground"
                value={colors.aiBackground}
                onChange={(e) => handleColorChange('aiBackground', e.target.value)}
              />
            </div>

            <div className="color-picker-group">
              <label htmlFor="aiText">{t('aiMsgText')}</label>
              <input
                type="color"
                id="aiText"
                value={colors.aiText}
                onChange={(e) => handleColorChange('aiText', e.target.value)}
              />
            </div>

            <div className="color-picker-group">
              <label htmlFor="iconColor">{t('iconColor')}</label>
              <input
                type="color"
                id="iconColor"
                value={colors.iconColor}
                onChange={(e) => handleColorChange('iconColor', e.target.value)}
              />
            </div>
          </div>
          <button className="reset-colors-button" onClick={handleResetColors}>
            {t('resetColors')}
          </button>
        </div>

        <div className="settings-section">
          <button className="export-chat-button" onClick={() => {}}>
            {t('exportChat')}
          </button>
        </div>

        <div className="settings-section">
          <h3>{t('language')}</h3>
          <select 
            value={currentLanguage}
            onChange={(e) => onLanguageChange(e.target.value)}
            className="language-selector"
          >
            {languages.map(lang => (
              <option key={lang.code} value={lang.code}>
                {`${lang.flag} ${lang.name}`}
              </option>
            ))}
          </select>
        </div>

        <div className="settings-section">
          <label htmlFor="model-selection">Primary Model</label>
          <select
            id="model-selection"
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
          >
            <option value="anthropic">Anthropic</option>
            <option value="llama">Llama</option>
          </select>
        </div>

        <div className="settings-section voice-selector-container">
          <label htmlFor="voice-selector">{t('voiceSelector')}</label>
          <select
            id="voice-selector"
            value={selectedVoice}
            onChange={(e) => setSelectedVoice(e.target.value)}
          >
            {voiceOptions.map((voice) => (
              <option key={voice.value} value={voice.value}>
                {voice.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel; 